import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { fullstorySelectors } from '@fullstory/redux';
import { useRequestQueryOptions } from '@fullstory/requests';
import { DQMRow } from '@fullstory/proto/fs/services/dx-objects';
import { getPageSuggestionsThumbnailUrlsRequest } from '../requests';
import { SignedThumbnailUrl } from '@fullstory/proto/fs/services/llm-tool';

const { getOrgId } = fullstorySelectors;

export const useSuggestedPageThumbnails = (
  rows: DQMRow[] = [],
  shouldFetch: boolean,
  maxUrlsPerSuggestion: number = 1,
) => {
  const orgId = useSelector(getOrgId);

  const suggestionIds = React.useMemo(
    () => rows.map(row => row.suggestionId).filter(suggestionId => !!suggestionId),
    [rows],
  );

  // Only fetch if shouldFetch is true, we have an orgId and suggestion IDs
  const enabled = shouldFetch && Boolean(orgId && suggestionIds.length > 0);

  const query = useQuery(
    useRequestQueryOptions(
      getPageSuggestionsThumbnailUrlsRequest({
        orgId: orgId || '',
        suggestionIds,
        maxUrlsPerSuggestion,
      }),
      {
        enabled,
        // Stale time set to 1 hour since presigned URLs expire after 1 hour
        staleTime: 60 * 60 * 1000, // 1 hour
      },
    ),
  );

  const thumbnailUrls = React.useMemo(
    () => query.data?.thumbnailsUrls || [],
    [query.data?.thumbnailsUrls],
  );

  const thumbnailUrlMap = React.useMemo(() => {
    const map = new Map<string, SignedThumbnailUrl[]>();
    thumbnailUrls.forEach(item => {
      if (item.suggestionId && item.signedUrls?.length) {
        map.set(item.suggestionId, item.signedUrls);
      }
    });
    return map;
  }, [thumbnailUrls]);

  // Helper function to get thumbnail URL for a specific suggestion ID
  const getThumbnailUrls = React.useCallback(
    (suggestionId: string) => thumbnailUrlMap.get(suggestionId),
    [thumbnailUrlMap],
  );

  return {
    thumbnailUrls,
    thumbnailUrlMap,
    getThumbnailUrls,
    isLoading: query.isLoading,
    isError: query.isError,
  };
};
